/* Models for the server-side multi-cost-center Actuals export (S&M only).
 *
 * The API contract is snake_case (see the design doc, contract C4). The service maps responses to the
 * camelCase `ActualsExport`; only the create request keeps the wire shape, because the request builder
 * produces it directly from the grid state.
 */

/** Job status values returned by the export API. */
export const ACTUALS_EXPORT_STATUSES = ['QUEUED', 'RUNNING', 'READY', 'FAILED', 'EXPIRED'] as const;
export type ActualsExportStatus = (typeof ACTUALS_EXPORT_STATUSES)[number];

/** Error codes of the API error body `{error, message}`. */
export const ACTUALS_EXPORT_API_ERROR_CODES = [
  'INVALID_REQUEST',
  'UNAUTHENTICATED',
  'NOT_ENTITLED',
  'ACCESS_CHANGED',
  'NOT_FOUND',
  'NOT_READY',
  'NOT_IN_PROGRESS',
  'EXPORT_IN_PROGRESS',
  'EXPIRED',
  'DAILY_CAP_REACHED',
  'SERVICE_DISABLED',
  'ROOTS_CHANGED',
  'DEPENDENCY_FAILURE'
] as const;
export type ActualsExportApiErrorCode = (typeof ACTUALS_EXPORT_API_ERROR_CODES)[number];

/** Codes the client assigns when a failure carries no usable API error code. */
export type ActualsExportClientErrorCode = 'NETWORK_ERROR' | 'NOT_CONFIGURED' | 'INVALID_RESPONSE' | 'THROTTLED' | 'UNKNOWN';

export type ActualsExportErrorCode = ActualsExportApiErrorCode | ActualsExportClientErrorCode;

export type ActualsExportSortDirection = 'asc' | 'desc';

export interface ActualsExportSortEntry {
  col_id: string;
  dir: ActualsExportSortDirection;
}

/** Body of `POST /actuals-exports`. Identity and access never travel in the body. */
export interface ActualsExportCreateRequest {
  business_group: 'SM';
  cost_centers: string[];
  columns?: string[];
  sort?: ActualsExportSortEntry[];
  time_zone?: string;
}

/** An export job, as the API reports it. Timestamps are ISO-8601 UTC strings. */
export interface ActualsExport {
  exportId: string;
  status: ActualsExportStatus;
  businessGroup: string | null;
  costCenterCount: number;
  costCenters: string[];
  fileName: string | null;
  rowCount: number | null;
  fileSize: number | null;
  dataAsOf: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  errorCode: string | null;
}

/** Response of `GET /actuals-exports/{export_id}/download`. The URL is never stored or logged. */
export interface ActualsExportDownload {
  url: string;
  expiresIn: number;
  fileName: string;
}

/** What the app remembers about the caller's latest export. Never holds the download URL. */
export interface ActualsExportTrackingState {
  exportId: string | null;
  status: ActualsExportStatus | null;
  ccCount: number | null;
  fileName: string | null;
  requestedAt: string | null;
  errorCode: string | null;
}
